import { useEffect, useState } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { Box, Link2, Loader2 } from "lucide-react";
import type { ContainerRepository } from "@/casts/ContainerRepository";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { ContainerRepositoryFactory } from "@/factories/ContainerRepositoryFactory";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { publish } from "@/services/nostr";

interface LinkContainerRepositoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repositories: ContainerRepository[];
  settled: boolean;
  repoCoordinates: string[];
  relayHint?: string;
}

function linksRepository(
  repository: ContainerRepository,
  repoCoordinates: string[],
): boolean {
  const coordinates = new Set(repoCoordinates);
  return repository.repoCoords.some((coordinate) =>
    coordinates.has(coordinate),
  );
}

export function LinkContainerRepositoryDialog({
  open,
  onOpenChange,
  repositories,
  settled,
  repoCoordinates,
  relayHint,
}: LinkContainerRepositoryDialogProps) {
  const account = useActiveAccount();
  const { toast } = useToast();
  const [selectedCoordinate, setSelectedCoordinate] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string>();
  const selectedRepository = repositories.find(
    (repository) => repository.coordinate === selectedCoordinate,
  );

  useEffect(() => {
    if (!open) return;
    setSelectedCoordinate("");
    setPublishing(false);
    setError(undefined);
  }, [open]);

  const handleLink = async () => {
    if (!account || !selectedRepository) return;
    if (selectedRepository.pubkey !== account.pubkey) {
      setError("Only the container repository publisher can link it.");
      return;
    }
    if (linksRepository(selectedRepository, repoCoordinates)) {
      setError("This container repository is already linked.");
      return;
    }

    setPublishing(true);
    setError(undefined);
    try {
      const linkedRepository =
        await ContainerRepositoryFactory.linkRepositories(
          selectedRepository.event,
          repoCoordinates,
          relayHint,
          Math.floor(Date.now() / 1000),
        ).sign(account.signer);
      await publish(linkedRepository, repoCoordinates);
      toast({
        title: "Container repository linked",
        description: `${selectedRepository.title} now appears in this repository's releases.`,
      });
      onOpenChange(false);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Failed to link container repository",
      );
      setPublishing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!publishing) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-h-[88vh] min-w-0 max-w-2xl overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle>All your container repositories</DialogTitle>
          <DialogDescription>
            Choose an ncontainer repository you already publish. Linking adds
            this Git repository’s address without changing image tags, Blossom
            servers, or other metadata.
          </DialogDescription>
        </DialogHeader>

        {!settled && repositories.length === 0 ? (
          <div className="space-y-3 py-2">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : repositories.length === 0 ? (
          <div className="rounded-xl border border-dashed px-6 py-10 text-center">
            <Box className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 font-medium">No container repositories found</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Publish an OCI layout with ncontainer first. GitWorkshop searches
              this account’s outbox and the current repository relays, without
              downloading or re-uploading its potentially large layers.
            </p>
          </div>
        ) : (
          <RadioGroup
            value={selectedCoordinate}
            onValueChange={setSelectedCoordinate}
            className="gap-3 py-2"
            aria-label="Container repository"
          >
            {repositories.map((repository) => {
              const linked = linksRepository(repository, repoCoordinates);
              const inputId = `link-container-${repository.event.id}`;
              return (
                <div
                  key={repository.coordinate}
                  className={cn(
                    "flex min-w-0 items-start gap-3 rounded-xl border p-4 transition-colors",
                    linked
                      ? "bg-muted/30"
                      : "hover:border-pink-500/50 hover:bg-muted/20",
                  )}
                >
                  <RadioGroupItem
                    id={inputId}
                    value={repository.coordinate}
                    disabled={linked || publishing}
                    className="mt-1 shrink-0"
                  />
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border bg-muted">
                    <Box className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <Label
                    htmlFor={inputId}
                    className={cn(
                      "block min-w-0 flex-1",
                      linked ? "cursor-default" : "cursor-pointer",
                    )}
                  >
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="truncate font-medium">
                        {repository.title}
                      </span>
                      {linked && <Badge variant="secondary">Linked</Badge>}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-xs font-normal text-muted-foreground">
                      {repository.name}
                    </span>
                    <span className="mt-2 flex flex-wrap gap-2 text-xs font-normal text-muted-foreground">
                      <span>
                        {repository.versions.length}{" "}
                        {repository.versions.length === 1 ? "tag" : "tags"}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>
                        {repository.servers.length}{" "}
                        {repository.servers.length === 1 ? "server" : "servers"}
                      </span>
                    </span>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        )}

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={publishing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleLink()}
            disabled={!selectedRepository || publishing}
          >
            {publishing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="mr-2 h-4 w-4" />
            )}
            Link to repository
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
